<template>
  <div class="flsa-steps-sidebar pa-8">
    <div class="text-h2 text-textDarkest mb-2">
      {{ sidebarTitle }}
    </div>

    <v-btn
      v-if="!readonly"
      variant="text"
      class="flsa-nav-item"
      :class="{ 'flsa-nav-item-active': currentSectionId === 'eligibility' }"
      @click="handleSectionChange('eligibility')"
    >
      <span class="text-h2 flsa-nav-label flsa-nav-label-section">Eligibility</span>
    </v-btn>

    <div
      v-if="!readonly"
      class="text-h2 flsa-exemptions-heading"
    >
      Exemptions
    </div>

    <template v-if="!readonly">
      <v-btn
        v-for="exemption in exemptionSections"
        :key="exemption.id"
        variant="text"
        class="flsa-nav-item flsa-nav-item-child"
        :class="{ 'flsa-nav-item-active': currentSectionId === exemption.id }"
        @click="handleSectionChange(exemption.id)"
      >
        <span class="text-body-1 flsa-nav-label flsa-nav-label-child">{{ exemption.label }}</span>
      </v-btn>
    </template>

    <v-btn
      variant="text"
      class="flsa-nav-item flsa-nav-item-summary"
      :class="{ 'flsa-nav-item-active': currentSectionId === 'summary' }"
      @click="handleSectionChange('summary')"
    >
      <span class="text-h2 flsa-nav-label flsa-nav-label-section">Summary</span>
    </v-btn>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { startCase } from 'lodash-es';
import { DEFAULT_FLSA_CONFIG } from '@/helpers/constants/flsaQuestions';
import { useTracking } from '@/stores/tracking/tracking';

const exemptionSections = DEFAULT_FLSA_CONFIG.sections
  .filter((section) => section.isExemption)
  .map((section) => ({ id: section.id, label: section.title }));

const validSections = [
  'eligibility',
  ...DEFAULT_FLSA_CONFIG.sections.map((section) => section.id),
  'summary',
];

const props = defineProps({
  section: {
    type: String,
    default: 'summary',
  },
  jurisdiction: {
    type: String,
    default: 'federal',
  },
  readonly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:section']);

const sidebarTitle = computed(() => (
  `${startCase(props.jurisdiction)} FLSA Form`
));

const currentSectionId = computed(() => (
  validSections.includes(props.section) ? props.section : 'summary'
));

const handleSectionChange = (sectionId) => {
  useTracking().flsaFormNavigatedSection({ section: sectionId });

  emit('update:section', sectionId);
};
</script>

<style scoped lang="less">
.flsa-steps-sidebar {
  min-height: 100%;
}

.flsa-exemptions-heading {
  color: @text-light;
  margin-top: 12px;
  margin-bottom: 4px;
  padding-left: 16px;
}

.flsa-nav-item {
  justify-content: flex-start;
  width: 100%;
  min-height: 36px;
  border-radius: 6px;
}

.flsa-nav-item-child {
  min-height: 40px;
  padding-left: 32px;
}

.flsa-nav-item-summary {
  margin-top: 8px;
}

.flsa-nav-label {
  letter-spacing: normal;
}

.flsa-nav-label-section {
  color: @text-light;
}

.flsa-nav-label-child {
  color: @text-darkest;
}

.flsa-nav-item-active {
  background: @bg-brand-light;

  .flsa-nav-label-section,
  .flsa-nav-label-child {
    color: @text-brand;
  }
}
</style>
